/**
 * Fiscal — emissão de NF-e e NFC-e.
 *
 * Esta tela tem UMA parte real: a confirmação de e-mail. `GET /eu` já traz
 * `email_confirmado`, e o próprio backend declara, na mensagem de
 * `POST /email/confirmacoes`, que "a emissão fiscal está liberada" quando o
 * e-mail é confirmado — ou seja, o sistema já assume essa dependência antes
 * de o módulo fiscal existir. Por isso o aviso aqui é honesto e não decorativo:
 * ele mostra o estado de verdade e, se faltar, reaproveita
 * `POST /email/reenvios` no mesmo padrão da `FaixaDeAviso` do Dashboard.
 *
 * O resto — certificado digital, fila de notas emitidas, configuração fiscal —
 * é o corpo do protótipo `Lumma Fit Fiscal.dc.html`, e nenhuma dessas coisas
 * tem uma linha de backend por trás: não existe módulo de emissão, nem de
 * certificado, nem fila, nem SPED. Fingir aqui — abas, KPIs, um "SEFAZ-SP
 * online" verde — seria inventar dado onde o mock do protótipo é só isso,
 * mock. Em vez disso, cada bloco mostra o estado vazio dizendo o que falta,
 * mesmo com o e-mail confirmado: confirmação de e-mail não é módulo fiscal.
 */
import { useState, type ReactNode } from 'react'

import { Icone, type NomeIcone } from '../../componentes/Icone'
import { api } from '../../plataforma/api'
import { useSessao } from '../../plataforma/contexto-sessao'
import { LayoutApp } from '../app/LayoutApp'

export function TelaFiscal() {
  const { situacao } = useSessao()
  const sessao = situacao.estado === 'autenticado' ? situacao.sessao : null

  return (
    <LayoutApp titulo="Fiscal" subtitulo="Emissão de NF-e e NFC-e">
      <div className="flex w-full flex-col gap-4">
        {sessao && (
          <FaixaDeEmail email={sessao.usuario.email} confirmado={sessao.email_confirmado} />
        )}

        <div className="grid items-start gap-4 xl:grid-cols-[1.55fr_1fr]">
          <Cartao titulo="Notas emitidas" subtitulo="NFC-e do balcão, NF-e de atacado e devoluções">
            <Vazio
              icone="receipt_long"
              texto="A emissão de NF-e e NFC-e ainda não existe no Lumma Store. Quando o módulo fiscal for construído, cada nota emitida pelo PDV aparece aqui, com número, destinatário, valor e situação na SEFAZ."
            />
          </Cartao>

          <div className="flex min-w-0 flex-col gap-4">
            <Cartao titulo="Certificado digital">
              <Vazio
                icone="lock"
                texto="Nenhum certificado foi cadastrado — o módulo de certificado digital ainda não existe. Sem ele, nenhuma nota pode ser assinada e enviada à SEFAZ."
              />
            </Cartao>

            <Cartao titulo="Configuração fiscal">
              <Vazio
                icone="edit_note"
                texto="Série e numeração de nota, regime tributário e o escritório contábil que recebe o pacote mensal dependem do módulo fiscal, que ainda não existe."
              />
            </Cartao>
          </div>
        </div>
      </div>
    </LayoutApp>
  )
}

/* ------------------------------------------------------------------ peças */

function Cartao({
  titulo,
  subtitulo,
  children,
}: {
  titulo: string
  subtitulo?: string
  children: ReactNode
}) {
  return (
    <section className="rounded-sistema border border-borda bg-superficie p-5 shadow-[var(--shadow-cartao)]">
      <div className="mb-4">
        <h2 className="font-titulo text-base font-semibold text-tinta">{titulo}</h2>
        {subtitulo && <p className="mt-[3px] text-[12.5px] text-texto-suave">{subtitulo}</p>}
      </div>
      {children}
    </section>
  )
}

/**
 * Estado vazio.
 *
 * Mesmo espírito do `Vazio` do Dashboard: diz o que ainda não existe em vez
 * de desenhar um número zerado ou uma tabela sem linha — as duas coisas se
 * leriam como "carregou e não tem nada", quando a verdade é "isto não existe
 * no sistema ainda".
 */
function Vazio({ icone, texto }: { icone: NomeIcone; texto: string }) {
  return (
    <div className="flex flex-col items-center gap-2.5 px-4 py-10 text-center">
      <Icone nome={icone} tamanho={26} className="text-inativo" />
      <p className="max-w-[360px] text-[13px] leading-[1.55] text-texto-suave">{texto}</p>
    </div>
  )
}

/**
 * Faixa de confirmação de e-mail.
 *
 * A única condição real desta tela hoje. Confirmado ou não, o texto nunca
 * promete emissão fiscal — só o que é verdade: o e-mail está confirmado (ou
 * não) e, sem módulo fiscal, isso ainda não emite nota nenhuma.
 */
function FaixaDeEmail({ email, confirmado }: { email: string; confirmado: boolean }) {
  const [enviado, setEnviado] = useState(false)

  const reenviar = async () => {
    try {
      await api.post('/email/reenvios', { email })
    } finally {
      // Sucesso ou falha, a tela responde igual — o servidor não conta se o
      // endereço existe, e a interface não pode desmentir isso com um erro
      // que só aparece para e-mail cadastrado (mesmo padrão do Dashboard).
      setEnviado(true)
    }
  }

  if (confirmado) {
    return (
      <div
        role="status"
        className="flex gap-3 rounded-sistema border border-ok-suave bg-ok-suave px-4 py-3.5"
      >
        <Icone nome="check_circle" tamanho={20} className="text-ok" />
        <div className="flex-1">
          <p className="text-sm font-semibold text-ok">E-mail confirmado</p>
          <p className="mt-[3px] text-[13px] leading-[1.55] text-ok">
            A confirmação de e-mail está feita. É a única condição já liberada para a emissão
            fiscal — o módulo que emite a nota em si ainda não existe, como mostram os cartões
            abaixo.
          </p>
        </div>
      </div>
    )
  }

  return (
    <div className="flex gap-3 rounded-sistema border border-atencao-suave bg-atencao-fundo px-4 py-3.5">
      <Icone nome="warning" tamanho={20} className="text-atencao-texto" />
      <div className="flex-1">
        <p className="text-sm font-semibold text-atencao-texto">E-mail não confirmado</p>
        <p className="mt-[3px] text-[13px] text-atencao-texto">
          Confirme o e-mail da conta para liberar a emissão fiscal assim que o módulo existir.
        </p>
      </div>
      <button
        type="button"
        disabled={enviado}
        onClick={() => void reenviar()}
        className="h-[34px] shrink-0 self-center rounded-sistema border border-atencao-suave bg-superficie px-3.5 text-[13px] font-semibold whitespace-nowrap text-atencao-texto hover:bg-atencao-fundo disabled:opacity-60"
      >
        {enviado ? 'Enviado' : 'Reenviar confirmação'}
      </button>
    </div>
  )
}
